/**
 * Create Activity Step Handler
 * Creates a sales activity/task for the entity
 */

import { supabase } from '../../supabase';
import type {
  StepExecutionContext,
  StepExecutionResult,
  CreateActivityStepConfig
} from '../../types/workflow';

export async function executeCreateActivity(
  context: StepExecutionContext
): Promise<StepExecutionResult> {
  try {
    const config = context.step.config as CreateActivityStepConfig;

    if (!config.activity_type || !config.description_template) {
      return {
        success: false,
        error: 'Missing required fields: activity_type or description_template',
      };
    }

    // Get entity data for variable substitution
    const entityData = context.entityData;

    // Replace variables in description template
    let description = config.description_template;
    description = description.replace(/\{entity_name\}/g, entityData.business_name || entityData.name || 'N/A');
    description = description.replace(/\{deal_value\}/g, entityData.amount?.toString() || '0');
    description = description.replace(/\{lead_name\}/g, entityData.business_name || 'N/A');

    // Calculate due date from entity creation time (e.g., when offer was created)
    // This ensures the deadline is based on the actual entity creation date
    const referenceDate = entityData.created_at
      ? new Date(entityData.created_at)
      : new Date(context.entity.entered_at); // Fallback to workflow entry time

    let dueDate: Date;

    if (config.round_to_30_minutes) {
      // Usa l'orario attuale come base e arrotonda ai 30 minuti successivi
      dueDate = new Date();

      // Aggiungi i giorni di offset
      if (config.due_date_offset_days) {
        dueDate.setDate(dueDate.getDate() + config.due_date_offset_days);
      }
      if (config.due_date_offset_hours) {
        dueDate.setHours(dueDate.getHours() + config.due_date_offset_hours);
      }

      // Arrotonda ai 30 minuti successivi
      const minutes = dueDate.getMinutes();
      if (minutes === 0 || minutes === 30) {
        dueDate.setSeconds(0, 0);
      } else if (minutes < 30) {
        dueDate.setMinutes(30, 0, 0);
      } else {
        dueDate.setHours(dueDate.getHours() + 1);
        dueDate.setMinutes(0, 0, 0);
      }
    } else {
      // Comportamento originale: usa la data dell'entità
      dueDate = new Date(referenceDate);
      if (config.due_date_offset_days) {
        dueDate.setDate(dueDate.getDate() + config.due_date_offset_days);
      }
      if (config.due_date_offset_hours) {
        dueDate.setHours(dueDate.getHours() + config.due_date_offset_hours);
      }
    }

    // Determine assignee
    let assignedTo = null;
    if (config.assign_to_current_user) {
      assignedTo = context.userId;
    } else if (config.assign_to_user_id) {
      assignedTo = config.assign_to_user_id;
    } else if (entityData.assigned_to) {
      // Default to entity's assigned user
      assignedTo = entityData.assigned_to;
    }

    // Create activity
    const activityData: any = {
      title: context.step.name,
      description,
      type: config.activity_type,
      status: config.status || 'pending',
      due_date: dueDate.toISOString(),
      assigned_to: assignedTo,
      user_id: context.userId,
      organization_id: context.organizationId,
    };

    // Determine target entity based on configuration
    const targetEntityType = config.target_entity_type || 'current';

    if (targetEntityType === 'current') {
      // Create on current workflow entity
      if (context.workflow.entity_type === 'lead') {
        activityData.lead_id = context.entity.entity_id;
      } else if (context.workflow.entity_type === 'deal') {
        activityData.deal_id = context.entity.entity_id;
      } else if (context.workflow.entity_type === 'client') {
        activityData.client_id = context.entity.entity_id;
      }
    } else if (targetEntityType === 'lead') {
      // Create on associated lead
      const leadId = entityData.lead_id || (context.workflow.entity_type === 'lead' ? context.entity.entity_id : null);
      if (leadId) {
        activityData.lead_id = leadId;
      } else {
        console.warn('No lead found, creating on current entity');
        activityData[`${context.workflow.entity_type}_id`] = context.entity.entity_id;
      }
    } else if (targetEntityType === 'deal') {
      // Create on associated deal
      const dealId = entityData.deal_id || entityData.id || (context.workflow.entity_type === 'deal' ? context.entity.entity_id : null);
      if (dealId && context.workflow.entity_type === 'deal') {
        activityData.deal_id = dealId;
      } else {
        console.warn('No deal found, creating on current entity');
        activityData[`${context.workflow.entity_type}_id`] = context.entity.entity_id;
      }
    }

    // Guard anti-duplicato: verifica che non esista già un'attività identica creata negli ultimi 30 secondi
    const entityIdField = activityData.lead_id ? 'lead_id' : activityData.client_id ? 'client_id' : activityData.deal_id ? 'deal_id' : null;
    const entityIdValue = activityData.lead_id || activityData.client_id || activityData.deal_id;
    if (entityIdField && entityIdValue) {
      const thirtySecondsAgo = new Date(Date.now() - 30000).toISOString();
      const { data: existing } = await supabase
        .from('sales_activities')
        .select('id')
        .eq(entityIdField, entityIdValue)
        .eq('title', activityData.title)
        .eq('type', activityData.type)
        .gte('created_at', thirtySecondsAgo)
        .limit(1);

      if (existing && existing.length > 0) {
        console.warn(`⚠️ Duplicate activity detected: "${activityData.title}" for ${entityIdField}=${entityIdValue}. Skipping creation.`);
        return {
          success: true,
          nextStepId: context.step.next_step_id,
          data: { activityId: existing[0].id, skippedDuplicate: true },
        };
      }
    }

    const { data: activity, error } = await supabase
      .from('sales_activities')
      .insert(activityData)
      .select()
      .single();

    if (error) {
      console.error('Error creating activity:', error);
      return {
        success: false,
        error: error.message,
      };
    }

    console.log(`✅ Activity created: ${activity.id}`);

    // TODO: Create Google Calendar event if config.create_calendar_event is true

    return {
      success: true,
      nextStepId: context.step.next_step_id,
      data: { activityId: activity.id },
    };
  } catch (error) {
    console.error('Error in createActivity handler:', error);
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}
